import { useEffect } from "react";

/**
 * Lightweight SEO hook — no `react-helmet` dependency.
 * Sets document.title, meta description, canonical link, and OG/Twitter
 * tags on mount. Restores previous values on unmount so navigating away
 * doesn't leave stale page-specific tags.
 *
 * Note: this only helps human browsers navigating the SPA. Social crawlers
 * (WhatsApp/LinkedIn/etc.) don't execute JS — for personalised previews
 * use a server-rendered route (see /api/r/{code} for the referral pattern).
 */
export default function useSeo({
    title,
    description,
    canonical,
    ogImage,
    jsonLd,       // optional: object → JSON-LD structured data
} = {}) {
    useEffect(() => {
        const prev = {};

        // Title
        if (title) {
            prev.title = document.title;
            document.title = title;
        }

        const setMeta = (attr, val, content) => {
            let el = document.head.querySelector(`meta[${attr}="${val}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, val);
                document.head.appendChild(el);
                prev[`__added_${attr}_${val}`] = el;
            } else {
                prev[`${attr}_${val}`] = el.getAttribute("content");
            }
            el.setAttribute("content", content);
        };

        if (description) {
            setMeta("name", "description", description);
            setMeta("property", "og:description", description);
            setMeta("name", "twitter:description", description);
        }
        if (title) {
            setMeta("property", "og:title", title);
            setMeta("name", "twitter:title", title);
            // V1.5.8.48 — every page that uses useSeo also gets a
            // large-summary Twitter card so LinkedIn / WhatsApp
            // unfurlers render a proper preview.
            setMeta("name", "twitter:card", "summary_large_image");
            setMeta("property", "og:type", "website");
            // V1.5.8.49 — parity with SeoMeta: every useSeo page also
            // carries og:site_name so social unfurlers render brand.
            setMeta("property", "og:site_name", "NaukriDaddy");
        }
        if (ogImage) {
            setMeta("property", "og:image", ogImage);
            setMeta("name", "twitter:image", ogImage);
        }

        // Canonical link
        let canonicalEl;
        if (canonical) {
            canonicalEl = document.head.querySelector('link[rel="canonical"]');
            if (canonicalEl) prev.canonical = canonicalEl.getAttribute("href");
            else {
                canonicalEl = document.createElement("link");
                canonicalEl.setAttribute("rel", "canonical");
                document.head.appendChild(canonicalEl);
                prev.__addedCanonical = canonicalEl;
            }
            canonicalEl.setAttribute("href", canonical);
            // V1.5.8.48 — mirror canonical into og:url for social unfurlers.
            setMeta("property", "og:url", canonical);
        }

        // JSON-LD structured data
        let scriptEl;
        if (jsonLd) {
            scriptEl = document.createElement("script");
            scriptEl.type = "application/ld+json";
            scriptEl.textContent = JSON.stringify(jsonLd);
            scriptEl.setAttribute("data-useseo", "1");
            document.head.appendChild(scriptEl);
        }

        // Cleanup
        return () => {
            if (prev.title != null) document.title = prev.title;
            if (prev.canonical != null && canonicalEl) canonicalEl.setAttribute("href", prev.canonical);
            if (prev.__addedCanonical) prev.__addedCanonical.remove();
            for (const key of Object.keys(prev)) {
                if (key.startsWith("__added_")) prev[key].remove();
                else if (!["title", "canonical"].includes(key) && !key.startsWith("__")) {
                    // restore meta tag content
                    const parts = key.match(/^(name|property)_(.+)$/);
                    if (parts) {
                        const el = document.head.querySelector(`meta[${parts[1]}="${parts[2]}"]`);
                        if (el) el.setAttribute("content", prev[key]);
                    }
                }
            }
            if (scriptEl) scriptEl.remove();
        };
    }, [title, description, canonical, ogImage, JSON.stringify(jsonLd)]);
}
